import React from 'react';
import { Box, VStack, HStack, Text, Badge, Alert, AlertIcon, AlertTitle, AlertDescription, Flex, Spinner } from '@chakra-ui/react';
import { FiAlertTriangle, FiClock, FiCalendar, FiCreditCard } from 'react-icons/fi';
import { usePlanRestriction } from '../context/PlanRestrictionContext';

const PlanInfoCard = ({ 
  showWarnings = true,
  size = "md",
  variant = "subtle"
}) => {
  const { restrictions } = usePlanRestriction();
  const { planInfo, isLoading: planInfoIsLoading } = restrictions;

  // Plan expiration logic
  const getPlanExpirationInfo = () => {
    // Check for different possible data structures
    const planData = planInfo?.data || planInfo;
    
    if (!planData) {
      return null;
    }
    
    const { 
      plan_name,
      plan,
      employee_limit,
      current_employees,
      remaining_employees,
      features,
      days_remaining,
      is_trial,
      is_expired,
      expires_at
    } = planData;
    
    // Handle expired plans
    if (is_expired) {
      return {
        status: 'expired',
        color: 'red',
        title: 'Plan Expired',
        description: 'Your plan has expired. Please renew to continue using the service.',
        icon: FiAlertTriangle,
        planDetails: {
          planName: plan_name,
          planType: plan,
          employeeLimit: employee_limit,
          currentEmployees: current_employees,
          remainingEmployees: remaining_employees,
          features: features,
          isTrial: is_trial
        }
      };
    }
    
    // Handle critical expiration (5 days or less)
    if (days_remaining <= 5) {
      return {
        status: 'critical',
        color: 'red',
        title: 'Plan Expiring Soon',
        description: `Your ${plan_name || (is_trial ? 'trial' : 'subscription')} expires in ${days_remaining} day${days_remaining === 1 ? '' : 's'}. Please renew to avoid service interruption.`,
        icon: FiAlertTriangle,
        planDetails: {
          planName: plan_name,
          planType: plan,
          employeeLimit: employee_limit,
          currentEmployees: current_employees,
          remainingEmployees: remaining_employees,
          features: features,
          isTrial: is_trial,
          daysRemaining: days_remaining,
          expiresAt: expires_at
        }
      };
    }
    
    // Active plan (no warning needed)
    return {
      status: 'active',
      color: 'green',
      title: 'Plan Active',
      description: `Your ${plan_name || (is_trial ? 'trial' : 'subscription')} is active.`,
      icon: FiCalendar,
      planDetails: {
        planName: plan_name,
        planType: plan,
        employeeLimit: employee_limit,
        currentEmployees: current_employees,
        remainingEmployees: remaining_employees,
        features: features,
        isTrial: is_trial,
        daysRemaining: days_remaining,
        expiresAt: expires_at
      }
    };
  };

  const planExpirationInfo = getPlanExpirationInfo();

  if (planInfoIsLoading) {
    return (
      <Box p={4} bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
        <Flex justify="center" align="center" p={4}>
          <Spinner size="md" color="blue.500" />
          <Text ml={3} color="gray.600">Loading plan information...</Text>
        </Flex>
      </Box>
    );
  }

  if (!planInfo) {
    return null;
  }

  return (
    <Box p={4} bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
      <VStack spacing={4} align="stretch">
        {/* Plan Info Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <FiCreditCard size={16} />
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              Plan Information
            </Text>
          </HStack>
          {planExpirationInfo && (
            <Badge colorScheme={planExpirationInfo.color} variant={variant} size="sm">
              {planExpirationInfo.status === 'expired' ? 'EXPIRED' : 
               planExpirationInfo.status === 'critical' ? 'CRITICAL' : 
               planExpirationInfo.status === 'warning' ? 'WARNING' : 'ACTIVE'}
            </Badge>
          )}
        </HStack>

        {/* Plan Details */}
        {planExpirationInfo && planExpirationInfo.planDetails && (
          <Box p={3} bg="gray.50" borderRadius="md">
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.600">Plan Type:</Text>
                <Text fontSize="xs" fontWeight="600" color="gray.700">
                  {planExpirationInfo.planDetails.planName}
                  {planExpirationInfo.planDetails.isTrial && (
                    <Badge ml={1} colorScheme="blue" variant="subtle" size="xs">Trial</Badge>
                  )}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.600">Expires in:</Text>
                <Text fontSize="xs" fontWeight="600" color="gray.700">
                  {planExpirationInfo.planDetails.daysRemaining} days
                </Text>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* Plan Expiration Warnings - Only show when 5 days or less remain */}
        {showWarnings && planExpirationInfo && (planExpirationInfo.status === 'expired' || planExpirationInfo.status === 'critical') && (
          <Alert status="error" borderRadius="md">
            <AlertIcon as={FiAlertTriangle} />
            <Box>
              <AlertTitle>{planExpirationInfo.title}</AlertTitle>
              <AlertDescription>
                {planExpirationInfo.description}
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default PlanInfoCard; 